import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import Image from '@stories/Components/Misc/Image/Image';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
export const defaultButtonStyles = css `
  display: inline-flex;
  align-items: center;
  border: var(--borderWidth, 0) solid var(--colorBorder, var(--colorBg));
  border-radius: ${fluid(24, 50)};
  background: var(--colorBg);
  background-image: linear-gradient(to right, var(--colorBgActive), var(--colorBgActive));
  background-repeat: no-repeat;
  background-size: var(--bgSize);
  background-position: left center;
  color: var(--colorFg);
  height: ${fluid(40, 50)};
  padding: 0px ${fluid(20, 32)};
  text-decoration: none;
  ${transition('background-size, border-color, opacity')};
  user-select: none;
  width: auto;
  position: relative;
  padding-top: 2px; // required to vertically center properly :(

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('color, background-color, border-color, transform')};

    span& {
      button:active &,
      a:active & {
        transform: translateY(1.5px);
      }
    }

    &:not(span) {
      &:active {
        transform: translateY(1.5px);
      }
    }
  }

  span& {
    button:focus-visible &,
    a:focus-visible & {
      outline: auto;
      outline-offset: 3px;
    }

    button:hover &,
    a:hover & {
      border-color: var(--colorBorderActive, var(--colorBgActive));
      color: var(--colorFgActive);
      background-size: var(--bgSizeActive);

      img {
        filter: invert(1);
      }
    }

    button:active &,
    a:active & {
      outline: none;
      background: var(--colorBgComplete);
    }
  }

  &:not(span) {
    cursor: pointer;

    &:focus-visible {
      outline: none;
    }

    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
      outline-style: solid;
      outline-color: var(--colorBgActive);
    }

    &:hover {
      border-color: var(--colorBorderActive, var(--colorBgActive));
      color: var(--colorFgActive);
      background-size: var(--bgSizeActive);
    }

    &:active {
      outline: none;
      color: var(--colorFgComplete);
      background: var(--colorBgComplete);
    }
  }
`;
export const orangeSolidVars = css `
  --borderWidth: 2px;

  --bgSize: 100% 100%;
  --colorFg: ${brand.white};
  --colorBg: ${brand.white};
  --colorBorder: ${brand.primary.orange};

  --bgSizeActive: 0% 100%;
  --colorFgActive: ${brand.primary.orange};
  --colorBgActive: ${brand.primary.orange};
  --colorBorderActive: ${brand.primary.orange};

  --colorFgComplete: ${brand.primary.orange};
  --colorBgComplete: ${brand.primary.blueGrey};
`;
export const blueSolidVars = css `
  --borderWidth: 2px;

  --bgSize: 100% 100%;
  --colorFg: ${brand.white};
  --colorBg: ${brand.white};
  --colorBorder: ${brand.primary.blue};

  --bgSizeActive: 0% 100%;
  --colorFgActive: ${brand.primary.blue};
  --colorBgActive: ${brand.primary.blue};
  --colorBorderActive: ${brand.primary.blue};

  --colorFgComplete: ${brand.primary.blue};
  --colorBgComplete: ${brand.primary.blueGrey};
`;
export const whiteSolidVars = css `
  --borderWidth: 2px;

  --bgSize: 100% 100%;
  --colorFg: ${brand.primary.blue};
  --colorBg: transparent;
  --colorBorder: ${brand.white};

  --bgSizeActive: 0% 100%;
  --colorFgActive: ${brand.white};
  --colorBgActive: ${brand.white};
  --colorBorderActive: ${brand.white};

  --colorFgComplete: ${brand.white};
  --colorBgComplete: ${rgba(brand.white, 0.1)};
`;
export const orangeOutlineVars = css `
  --borderWidth: 2px;

  --bgSize: 0% 100%;
  --colorFg: ${brand.primary.orange};
  --colorBg: transparent;
  --colorBorder: ${brand.primary.orange};

  --bgSizeActive: 100% 100%;
  --colorFgActive: ${brand.white};
  --colorBgActive: ${brand.primary.orange};
  --colorBorderActive: ${brand.primary.orange};

  --colorFgComplete: ${brand.primary.blue};
  --colorBgComplete: ${brand.primary.orange};
`;
export const blueOutlineVars = css `
  --borderWidth: 2px;

  --bgSize: 0% 100%;
  --colorFg: ${brand.primary.blue};
  --colorBg: transparent;
  --colorBorder: ${brand.primary.blue};

  --bgSizeActive: 100% 100%;
  --colorFgActive: ${brand.white};
  --colorBgActive: ${brand.primary.blue};
  --colorBorderActive: ${brand.primary.blue};

  --colorFgComplete: ${brand.white};
  --colorBgComplete: ${brand.base.blue};
`;
export const whiteOutlineVars = css `
  --borderWidth: 2px;

  --bgSize: 0% 100%;
  --colorFg: ${brand.white};
  --colorBg: transparent;
  --colorBorder: ${brand.white};

  --bgSizeActive: 100% 100%;
  --colorFgActive: ${brand.primary.blue};
  --colorBgActive: ${brand.white};
  --colorBorderActive: ${brand.white};

  --colorFgComplete: ${brand.primary.blue};
  --colorBgComplete: ${brand.primary.blueGrey};
`;
const Container = styled.button `
  ${defaultButtonStyles}

  &[data-fill='right'] {
    background-position: left center;
  }

  &[data-fill='left'] {
    background-position: right center;
  }

  &[data-size='small'] {
    height: ${fluid(40, 40)};
    padding: 2px 14px 0;
  }

  &[data-type='orange-solid'] {
    ${orangeSolidVars}
  }

  &[data-type='blue-solid'] {
    ${blueSolidVars}
  }

  &[data-type='white-solid'] {
    ${whiteSolidVars}
  }

  &[data-type='orange-outline'] {
    ${orangeOutlineVars}
  }

  &[data-type='blue-outline'] {
    ${blueOutlineVars}
  }

  &[data-type='white-outline'] {
    ${whiteOutlineVars}
  }

  &[data-icon-only='true'] {
    height: ${fluid(40, 50)};
    width: ${fluid(40, 50)};
    justify-content: center;
    padding: 0;
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    background-size: 100% 100%;
    opacity: 0.5;
    pointer-events: none;
  }

  @media print {
    display: none !important;
  }
`;
const Text = styled.span `
  ${transition('color')};

  color: currentColor;
  ${fonts.apparat.semiBold};
  font-size: ${fluid(16, 18)};
  line-height: 1em;
  text-decoration: none;
  text-transform: uppercase;

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color')};

  --size: ${fluid(14, 20)};
  --spacing: 8px;

  display: flex;
  color: currentColor;
  height: var(--size);
  margin: -2px 0 0;
  padding: 0;
  flex-shrink: 0;

  [data-icon-position='left'] & {
    margin-right: auto;
    order: -1;
    padding-right: var(--spacing);
  }

  [data-icon-position='right'] & {
    margin-left: auto;
    order: 1;
    padding-left: var(--spacing);
  }

  [data-icon-only='true'] & {
    color: currentColor;
    height: var(--size);
    margin: 0;
    padding: 0;
    width: var(--size);
  }

  img,
  svg {
    display: block;
    width: var(--size);
    height: var(--size);
    transform: translateY(-10%);
  }
`;
const ButtonImage = styled(Image) ``;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
    ButtonImage,
};
export default ButtonStyles;
